/** biome-ignore-all lint/nursery/noExcessiveLinesPerFunction: <> */

import _debounce from 'lodash/debounce';
import { DeviceSize } from '@/app_lib/hooks/useMediaQuery';
import { calculateBrowserWidth } from '@/app_lib/utils/clientUtils';

// Configuration constants
const timingConfig = {
  leaderboardOpenTimer: 3000,
  loadTimePoll: 250,
  scrollDebounce: 200,
  scrollThreshold: 25,
} as const;

const breakpoints = {
  desktopAdWidth: 820,
  desktopMin: 1024,
  mobileAdWidth: 576,
  tabletMin: 768,
} as const;

const directSoldDimensions = {
  desktop: { height: 90, width: 970 },
  mobile: { height: 50, width: 300 },
} as const;

const defaultHeights = {
  desktop: 300,
  mobile: 110,
  tablet: 160,
} as const;

// DOM selectors
const selectors = {
  directSoldIframe: '.cc-height iframe',
  leaderboard: '#Leaderboard0',
  leaderboardIframe: '.leaderboard-ad-wrapper-parent iframe',
  parentLeader: '.leaderboard-ad-wrapper-parent',
  root: ':root',
  stickyHub: '.rr-sticky-hub',
} as const;

// CSS classes
const cssClasses = {
  ccHeight: 'cc-height',
  isSticky: 'is-sticky',
  isStickyCcHeight: 'is-sticky-cc-height',
  large: 'lg',
  leaderboardScroll: 'leaderboard--scroll',
  leaderboardSticky: 'leaderboard--sticky',
  test: 'leaderboard-ad-wrapper-parent-test',
} as const;

type PossibleClasses = typeof cssClasses.isSticky | typeof cssClasses.isStickyCcHeight | typeof cssClasses.large;

/**
 * Calculates the appropriate leaderboard height based on screen width
 */
function getLeaderboardHeight(clientWidth: number): number {
  if (clientWidth >= DeviceSize.Laptop) {
    return defaultHeights.desktop;
  }

  if (clientWidth >= DeviceSize.Tablet) {
    return defaultHeights.tablet;
  }

  return defaultHeights.mobile;
}

/**
 * Checks if an ad is a direct sold ad based on its dimensions
 */
function isDirectSoldAd(width: number | undefined, height: number | undefined): boolean {
  if (!(width && height)) {
    return false;
  }

  const isDesktopDirectSold =
    width === directSoldDimensions.desktop.width && height === directSoldDimensions.desktop.height;

  const isMobileDirectSold =
    width === directSoldDimensions.mobile.width && height === directSoldDimensions.mobile.height;

  return isDesktopDirectSold || isMobileDirectSold;
}

/**
 * Sets CSS custom property for leaderboard height
 */
function setLeaderboardHeight(height: number): void {
  const root = document.querySelector<HTMLElement>(selectors.root);
  root?.style.setProperty('--ld-height', `${height}px`);
}

/**
 * Handles scroll behavior for direct sold ads
 */
function handleDirectSoldScroll(leaderboardHeight: number): void {
  const directSoldIframe = document.querySelector(selectors.directSoldIframe) as HTMLElement;

  if (directSoldIframe?.clientHeight) {
    const currentHeight = directSoldIframe.clientHeight;
    const heightToSet = window.scrollY === 0 ? leaderboardHeight : currentHeight;
    setLeaderboardHeight(heightToSet);
  } else {
    setLeaderboardHeight(0);
  }
}

/**
 * Sets up scroll listener for direct sold ads
 */
function setupDirectSoldScrollListener(leaderboardHeight: number): void {
  const debouncedScrollHandler = _debounce(
    () => handleDirectSoldScroll(leaderboardHeight),
    timingConfig.scrollDebounce,
  );

  document.addEventListener('scroll', debouncedScrollHandler);
}

/**
 * Handles direct sold ad behavior
 */
function handleDirectSoldAd(leaderboardHeight: number, parentLeader: Element): void {
  setTimeout(() => {
    if (parentLeader.classList.contains(cssClasses.ccHeight)) {
      requestAnimationFrame(() => {
        parentLeader.classList.remove(cssClasses.leaderboardScroll);
        setLeaderboardHeight(leaderboardHeight);
        setupDirectSoldScrollListener(leaderboardHeight);
      });
    }
  }, timingConfig.leaderboardOpenTimer);
}

/**
 * Creates scroll listener for non-direct sold ads
 */
function createNonDirectSoldScrollListener(parentLeader: Element): () => void {
  return () => {
    if (window.scrollY > timingConfig.scrollThreshold) {
      parentLeader.classList.add(cssClasses.leaderboardScroll);
      document.removeEventListener('scroll', createNonDirectSoldScrollListener(parentLeader));
    }
  };
}

/**
 * Applies sticky hub classes based on ad type and conditions
 */
function applyStickyHubClasses(
  isDirectSold: boolean,
  parentLeader: Element,
  clientWidth: number,
  stickyFlag: boolean,
): void {
  const stickyHub = document.querySelector(selectors.stickyHub);
  if (!stickyHub) {
    return;
  }

  const hasConsentHeight = parentLeader.classList.contains(cssClasses.ccHeight);

  if (isDirectSold || hasConsentHeight) {
    stickyHub.classList.add(cssClasses.isStickyCcHeight, cssClasses.large);
  } else {
    const classesToAdd: PossibleClasses[] = [cssClasses.isSticky];
    if (!stickyFlag || clientWidth < breakpoints.mobileAdWidth) {
      classesToAdd.push(cssClasses.large);
    }
    stickyHub.classList.add(...classesToAdd);
  }
}

/**
 * Handles non-direct sold ad behavior
 */
function handleNonDirectSoldAd(
  leaderboardHeight: number,
  adHeight: number | undefined,
  clientWidth: number,
  parentLeader: Element,
): void {
  let stickyFlag = true;

  // Initial setup
  if (stickyFlag) {
    setLeaderboardHeight(leaderboardHeight);
    parentLeader.classList.add(cssClasses.leaderboardSticky);

    if (clientWidth <= breakpoints.desktopAdWidth && adHeight) {
      requestAnimationFrame(() => {
        setLeaderboardHeight(adHeight);
        const scrollListener = createNonDirectSoldScrollListener(parentLeader);
        document.addEventListener('scroll', scrollListener);
      });
    }
  }

  // Remove sticky behavior after timer
  setTimeout(() => {
    stickyFlag = false;
    if (clientWidth > breakpoints.desktopAdWidth) {
      parentLeader.classList.remove(cssClasses.leaderboardSticky);
      setLeaderboardHeight(0);
    }
  }, timingConfig.leaderboardOpenTimer);

  // Apply sticky hub classes
  if (stickyFlag && clientWidth >= breakpoints.mobileAdWidth) {
    setTimeout(() => {
      applyStickyHubClasses(false, parentLeader, clientWidth, stickyFlag);
    }, timingConfig.leaderboardOpenTimer);
  }
}

/**
 * Applies initial sticky behavior to the leaderboard
 */
export function applyInitialStickyBehavior() {
  const parentLeader = document.querySelector(selectors.parentLeader);
  // Set initial leaderboard height
  const clientWidth = calculateBrowserWidth();
  const leaderboardHeight = getLeaderboardHeight(clientWidth);
  setLeaderboardHeight(leaderboardHeight);

  // Add sticky class to parent leaderboard element
  parentLeader?.classList?.add(cssClasses.leaderboardSticky);
  parentLeader?.classList?.add(cssClasses.test);
}

/**
 * Main leaderboard manager function
 */
export const leaderboardManager = (): void => {
  const leaderboardCheck = setInterval(() => {
    // Get DOM elements and measurements
    const clientWidth = calculateBrowserWidth();
    const parentLeader = document.querySelector(selectors.parentLeader);
    const leaderboardIframe = document.querySelector(selectors.leaderboardIframe) as HTMLIFrameElement;
    const leaderboardHeight = getLeaderboardHeight(clientWidth);

    if (!(parentLeader && leaderboardIframe)) {
      return;
    }

    // Check if the ad inside the Google iframe has loaded
    let adLoaded = false;
    // Attempt to access the iframe's document
    const iframeDoc = leaderboardIframe.contentDocument;
    if (iframeDoc) {
      adLoaded = leaderboardIframe.clientHeight > 0 && leaderboardIframe.clientWidth > 0;
    }

    if (!adLoaded) {
      return;
    }

    const adWidth = leaderboardIframe.clientWidth;
    const adHeight = leaderboardIframe.clientHeight;
    if (!(adWidth && adHeight)) {
      return;
    }
    const isDirectSold = isDirectSoldAd(adWidth, adHeight);
    // Leaderboard has loaded, stop polling
    clearInterval(leaderboardCheck);
    // Handle different ad types
    if (isDirectSold) {
      handleDirectSoldAd(leaderboardHeight, parentLeader);
      applyStickyHubClasses(true, parentLeader, clientWidth, true);
    } else {
      handleNonDirectSoldAd(leaderboardHeight, adHeight, clientWidth, parentLeader);
    }
  }, timingConfig.loadTimePoll);
};
